import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import {
  fetchAudienceByGlobalMarket,
  clearAudienceByGlobalMarket,
} from 'redux/analytics/actions';

import { Loader } from 'semantic-ui-react';
import { HorizontalBar } from 'react-chartjs-2';
import AnalyticsSection from 'components/dashboard/analytics-content/AnalyticsSection';
import AnalyticsPlaceholder from 'components/dashboard/analytics-content/AnalyticsPlaceholder';
import AnalyticsLockedContent from 'components/dashboard/analytics-content/AnalyticsLockedContent';
import { continents as continentsFakeData } from 'components/dashboard/analytics-content/AnalyticsLockedContent/fakeData.js';

const AnalyticsAudienceByContinents = ({
  date,
  audienceByGlobalMarket,
  audienceByGlobalMarketError,
  fetchAudienceByGlobalMarket,
  clearAudienceByGlobalMarket,
  me,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    fetchAudienceByGlobalMarket(date);
    return () => clearAudienceByGlobalMarket();
  }, [date]);

  if (audienceByGlobalMarketError) {
    return null;
  }

  if (audienceByGlobalMarket && Object.keys(audienceByGlobalMarket).length === 0 && me.subscription.ff_advanced_analytics) {
    return <AnalyticsPlaceholder title={t("Views by Global Market")} text={t("Looks like you have no data to display yet…")} />;
  }

  const renderHorizontalBar = (audienceByGlobalMarketData) => {
    if (!audienceByGlobalMarketData) {
      return null;
    }

    const options = {
      legend: {
        display: false,
      },
      tooltips: {
        yPadding: 10,
        xPadding: 10,
        titleFontStyle: 'normal',
        callbacks: {
          label: () => null,
          title: tooltipItem => `${tooltipItem[0].yLabel}: ${audienceByGlobalMarketData[tooltipItem[0].yLabel].percent}% (${audienceByGlobalMarketData[tooltipItem[0].yLabel].views} ${t("views")})`,
        },
      },
      scales: {
        yAxes: [{
          maxBarThickness: 20,
          ticks: {
            padding: 20,
            beginAtZero: true,
            fontColor: '#2d303b',
            fontSize: 14,
            fontWeight: 600,
          },
          gridLines: {
            display: false,
            drawBorder: false,
          },
        }],
        xAxes: [{
          ticks: {
            padding: 20,
            suggestedMin: 0,
            suggestedMax: 100,
            beginAtZero: true,
            callback: value => `${value} %`,
            fontColor: '#8e9aaa',
          },
          gridLines: {
            color: '#eaeced',
            drawBorder: false,
          },
        }],
      },
    };

    const data = {
      labels: Object.keys(audienceByGlobalMarketData).map(i => i),
      datasets: [
        {
          data: Object.keys(audienceByGlobalMarketData).map(i => audienceByGlobalMarketData[i].percent),
          label: '',
          backgroundColor: '#239ae7',
          hoverBackgroundColor: '#2975cf',
          borderWidth: 0,
        }
      ],
    };
    return (
      <HorizontalBar
        data={data}
        options={options}
        height={130}
      />
    );
  };

  return (
    <AnalyticsSection
      className="analytics-audience-by-global-market__component"
      header={
        <div className="title">
          <h3>{t("Views by Global Market")}</h3>
        </div>
      }
    >
      <div className="analytics-audience-by-global-market-content">
        {!me.subscription.ff_advanced_analytics ?
          <AnalyticsLockedContent>
            {renderHorizontalBar(continentsFakeData)}
          </AnalyticsLockedContent>
          :
          <>
            {renderHorizontalBar(audienceByGlobalMarket)}
          </>
        }
        <Loader active={!audienceByGlobalMarket} />
      </div>
    </AnalyticsSection>
  );
};

AnalyticsAudienceByContinents.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchAudienceByGlobalMarket: PropTypes.func.isRequired,
  clearAudienceByGlobalMarket: PropTypes.func.isRequired,
  audienceByGlobalMarket: PropTypes.oneOfType([PropTypes.object]),
  audienceByGlobalMarketError: PropTypes.oneOfType([PropTypes.object]),
  me: PropTypes.object.isRequired,
};
AnalyticsAudienceByContinents.defaultProps = {
  audienceByCountry: null,
  audienceByCountryError: null,
};

const mapStateToProps = state => ({
  audienceByGlobalMarket: state.analyticsReducer.audienceByGlobalMarket,
  audienceByGlobalMarketError: state.analyticsReducer.audienceByGlobalMarketError,
  me: state.usersReducer.me,
});

export default connect(
  mapStateToProps,
  {
    fetchAudienceByGlobalMarket,
    clearAudienceByGlobalMarket,
  },
)(AnalyticsAudienceByContinents);
